import { useFilter } from "@/context/FilterProvider";
import { trackLegacyEvent } from "@analytics";
import {
  ButtCon,
  Button,
  Drawer,
  InputList,
  Link,
  Stack,
  TextInput,
} from "@biom3/react";
import {
  useIsLessThanMediumScreen,
  useIsLessThanXSmallScreen,
} from "@ui-kit/hooks/screenSizeHooks";
import { useEffect, useState } from "react";
import {
  createFilterState,
  createFiltersFromSelected,
  handleFilterChange,
} from "./FilterMenu";

export function MobileFilterMenu() {
  const isSmallScreen = useIsLessThanMediumScreen();
  const isXSmallScreen = useIsLessThanXSmallScreen();
  const { filters, setFilters, filterConfig } = useFilter();
  const [filterCount, setFilterCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, Set<string>>
  >(createFilterState(filters));

  useEffect(() => {
    const count = Object.values(filters).reduce(
      (acc, values) => acc + (values?.length || 0),
      0,
    );
    setFilterCount(count);
  }, [filters]);

  const handleClear = (closeDrawer: () => void) => {
    const emptyFilters = {
      platform: [],
      genre: [],
      search: [],
    };
    setFilters(emptyFilters);
    setSelectedFilters(createFilterState(emptyFilters));
    setSearchTerm("");

    trackLegacyEvent({
      screen: "GamesList",
      userJourney: "Games",
      action: "Changed",
      control: "ClearFilters",
      controlType: "Button",
    });

    closeDrawer();
  };

  const handleApply = (closeDrawer: () => void) => {
    const newFilters = createFiltersFromSelected(
      selectedFilters,
      searchTerm ? [searchTerm.toLowerCase()] : [],
    );
    setFilters(newFilters);

    trackLegacyEvent({
      screen: "GamesList",
      userJourney: "Games",
      action: "Changed",
      control: "Filters",
      controlType: "Button",
      extras: {
        platform: Array.from(selectedFilters.platform || []),
        genre: Array.from(selectedFilters.genre || []),
        search: searchTerm ? searchTerm.toLowerCase() : null,
        filterType: "mobile",
      },
    });

    closeDrawer();
  };

  const onFilterChange = (filterKey: string, value: string) => {
    const newSelected = handleFilterChange(selectedFilters, filterKey, value);
    setSelectedFilters((prev) => ({
      ...prev,
      [filterKey]: newSelected,
    }));
  };

  return (
    <Drawer
      drawerPosition={isSmallScreen ? "bottom" : "right"}
      size={isSmallScreen ? "full" : "half"}
      bgOverlaySx={{ backgroundColor: "base.color.translucent.inverse.700" }}
      headerBarTitle="Filter games"
      containerSx={{ zIndex: 35 }}
    >
      <Drawer.Target
        sx={{
          position: "absolute",
          right: "0",
          top: "0",
        }}
      >
        {isXSmallScreen ? (
          <ButtCon icon="Filter" size="medium" variant="tertiary" />
        ) : (
          <Button size="small" variant="tertiary">
            <Button.Icon icon="Filter" />
            {`Filter ${filterCount > 0 ? `(${filterCount})` : ""}`}
          </Button>
        )}
      </Drawer.Target>
      <Drawer.Content>
        {({ closeDrawer }) => (
          <Stack
            sx={{
              height: "100%",
              width: "100%",
            }}
            gap="0"
          >
            <Stack sx={{ overflowY: "auto", p: "base.spacing.x4" }}>
              <TextInput
                placeholder="Title"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sizeVariant="medium"
                sx={{ mb: "base.spacing.x4" }}
                hideClearValueButton={true}
              >
                <TextInput.Icon icon="Search" />
              </TextInput>
              {Object.entries(filterConfig).map(([key, config]) => (
                <InputList
                  key={key}
                  hideFormControlDividers
                  sx={{ mb: "base.spacing.x4" }}
                >
                  <InputList.Label>
                    {config?.title?.toUpperCase()}
                  </InputList.Label>
                  {config?.options?.map((option) => (
                    <InputList.FormControl key={option.value} textAlign="right">
                      <InputList.FormControl.Label>
                        {option.label}
                      </InputList.FormControl.Label>
                      <InputList.inputs.Checkbox
                        checked={selectedFilters[key]?.has(option.value)}
                        onChange={() => onFilterChange(key, option.value)}
                      />
                    </InputList.FormControl>
                  ))}
                </InputList>
              ))}
            </Stack>
            <Stack
              sx={{
                p: "base.spacing.x4",
                justifyContent: "space-between",
                mt: "auto",
                backgroundColor: "base.color.translucent.inverse.800",
              }}
              direction="row"
            >
              <Link
                variant="secondary"
                onClick={() => handleClear(closeDrawer)}
                sx={{ mx: "base.spacing.x2", py: "base.spacing.x3" }}
                size="medium"
              >
                <Link.Icon icon="Close" variant="bold" />
                Clear
              </Link>
              <Button
                variant="secondary"
                onClick={() => handleApply(closeDrawer)}
                sx={{ mx: "base.spacing.x2" }}
                size="medium"
              >
                Filter
              </Button>
            </Stack>
          </Stack>
        )}
      </Drawer.Content>
    </Drawer>
  );
}
